import { useCallback, useEffect, useState } from "react";
import { ApiError } from "./api";
import { errorKey, useI18n, type Key } from "./i18n";
import * as passkeys from "./passkeys";

/** Default name taken from the device itself, so nobody has to think of one. */
function deviceKey(): Key {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "device.iphone";
  if (/iPad/.test(ua)) return "device.ipad";
  if (/Android/.test(ua)) return "device.android";
  if (/Mac/.test(ua)) return "device.mac";
  if (/Windows/.test(ua)) return "device.windows";
  return "device.generic";
}

export default function PasskeyPanel({ onAuthError }: { onAuthError: (err: unknown) => boolean }) {
  const { lang, t } = useI18n();
  const [items, setItems] = useState<passkeys.SavedPasskey[]>([]);
  const [available, setAvailable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setItems(await passkeys.list());
    } catch (err) {
      if (!onAuthError(err)) {
        setError(t(err instanceof ApiError ? errorKey(err.code) : "error.passkey_list_failed"));
      }
    }
  }, [onAuthError, t]);

  useEffect(() => {
    void passkeys.isAvailable().then(setAvailable);
    void load();
  }, [load]);

  const register = useCallback(async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await passkeys.register(t(deviceKey()));
      setNotice(t("passkey.registered"));
      await load();
    } catch (err) {
      if (!onAuthError(err)) {
        setError(t(err instanceof ApiError ? errorKey(err.code) : "error.passkey_register_failed"));
      }
    } finally {
      setBusy(false);
    }
  }, [load, onAuthError, t]);

  const remove = useCallback(
    async (id: string) => {
      const previous = items;
      setItems((xs) => xs.filter((x) => x.id !== id));
      try {
        await passkeys.remove(id);
      } catch (err) {
        setItems(previous);
        if (!onAuthError(err)) {
          setError(t(err instanceof ApiError ? errorKey(err.code) : "error.passkey_delete_failed"));
        }
      }
    },
    [items, onAuthError, t],
  );

  return (
    <section className="rounded border border-current/20 p-5">
      <h2 className="text-xs font-semibold uppercase tracking-widest opacity-60">
        {t("passkey.title")}
      </h2>
      <p className="mt-2 max-w-prose text-sm opacity-70">{t("passkey.blurbLong")}</p>

      {!available && (
        <p className="mt-3 text-sm text-[var(--color-warn)]">{t("passkey.unsupportedLong")}</p>
      )}

      {error && <p role="alert" className="mt-3 text-[var(--color-bad)]">{error}</p>}
      {notice && <p className="mt-3 text-[var(--color-accent)]">{notice}</p>}

      {items.length > 0 && (
        <ul className="mt-4 flex flex-col gap-2">
          {items.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-3 rounded border border-current/15 px-3 py-2"
            >
              <span className="min-w-0">
                <span className="block truncate font-medium">{p.label}</span>
                <span className="block text-sm opacity-60">
                  {p.lastUsedAt
                    ? t("passkey.usedOn", {
                        date: new Date(p.lastUsedAt).toLocaleDateString(lang),
                      })
                    : t("passkey.unused")}
                  {" · "}
                  {t(p.backedUp ? "passkey.backedUpShort" : "passkey.thisDeviceOnly")}
                </span>
              </span>
              <button
                type="button" onClick={() => void remove(p.id)}
                aria-label={t("passkey.removeLabel", { label: p.label })}
                className="shrink-0 rounded px-2 py-1 text-sm opacity-60 hover:bg-current/10 hover:opacity-100"
              >
                {t("passkey.remove")}
              </button>
            </li>
          ))}
        </ul>
      )}

      {available && (
        <button
          type="button" onClick={() => void register()} disabled={busy}
          className="mt-4 rounded bg-[var(--color-accent)] px-5 py-2.5 font-semibold text-white disabled:opacity-50"
        >
          {busy
            ? t("passkey.registering")
            : t(items.length ? "passkey.registerAnother" : "passkey.enable")}
        </button>
      )}
    </section>
  );
}
